import React, { useContext } from 'react';
import { AppContext } from '../../App';
import { TrendingUp, TrendingDown, Minus, Trophy } from 'lucide-react';

const TopModelsTable = () => {
  const { dashboardData } = useContext(AppContext);
  
  if (!dashboardData) return null;
  
  const { popular_models } = dashboardData;

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-[#22C55E]" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-[#EF4444]" />;
    return <Minus className="w-4 h-4 text-[#A1A1AA]" />;
  };

  const getDaysSupplyBadge = (days) => {
    if (days < 20) return { color: 'bg-[#EF4444]/20 text-[#EF4444]', label: 'Hot' };
    if (days < 35) return { color: 'bg-[#F97316]/20 text-[#F97316]', label: 'Warm' };
    return { color: 'bg-[#22C55E]/20 text-[#22C55E]', label: 'Stable' };
  };

  // Mini bar for relative comparison
  const maxCount = Math.max(...popular_models.map(m => m.count));

  return (
    <div className="dashboard-card p-6" data-testid="top-models">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-[#F97316]/10 rounded-lg">
          <Trophy className="w-5 h-5 text-[#F97316]" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Top Performing Models</h3>
          <p className="text-sm text-[#A1A1AA]">Ranked by inventory volume</p>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#52525B] border-b border-[#27272A]">
              <th className="pb-3 font-medium w-8">#</th>
              <th className="pb-3 font-medium">Make / Model</th>
              <th className="pb-3 font-medium">Volume</th>
              <th className="pb-3 font-medium text-right">Avg Price</th>
              <th className="pb-3 font-medium text-right">Days Supply</th>
              <th className="pb-3 font-medium text-right">7d Change</th>
            </tr>
          </thead>
          <tbody>
            {popular_models.map((model, index) => {
              const supplyBadge = getDaysSupplyBadge(model.days_supply);
              const barWidth = (model.count / maxCount) * 100;
              
              return (
                <tr 
                  key={index}
                  className="border-b border-[#27272A]/50 hover:bg-white/5 transition-colors"
                  data-testid={`model-row-${index}`}
                >
                  <td className="py-4">
                    <span className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold ${
                      index < 3 ? 'bg-[#F97316]/20 text-[#F97316]' : 'bg-[#27272A] text-[#A1A1AA]'
                    }`}>
                      {model.rank}
                    </span>
                  </td>
                  <td className="py-4">
                    <div>
                      <p className="text-white font-medium">{model.make} {model.model}</p>
                    </div>
                  </td>
                  <td className="py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-2 bg-[#27272A] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#3B82F6] rounded-full transition-all duration-500"
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                      <span className="text-white font-mono text-xs">{model.count.toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="py-4 text-right">
                    <span className="text-white font-mono">${model.avg_price.toLocaleString()}</span>
                  </td>
                  <td className="py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${supplyBadge.color}`}>
                        {supplyBadge.label}
                      </span>
                      <span className="text-[#A1A1AA] font-mono text-xs">{model.days_supply}d</span>
                    </div>
                  </td>
                  <td className="py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {getTrendIcon(model.trend)}
                      <span className={`font-mono text-xs ${
                        model.trend === 'up' ? 'text-[#22C55E]' : 
                        model.trend === 'down' ? 'text-[#EF4444]' : 'text-[#A1A1AA]'
                      }`}>
                        {model.change_percent > 0 ? '+' : ''}{model.change_percent}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TopModelsTable;
